class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.heading = this.querySelector('.cart-notification__heading');
    this.defaultHeading = this.heading?.textContent.trim() || '';
    this.defaultCartLinkText = document.getElementById('cart-notification-button')?.textContent.trim() || 'View cart';
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState, options = {}) {
    // Adds can return one line or an items array; changes return a full cart.
    // Full carts must use the transaction's target, never an unrelated first item.
    const items = Array.isArray(parsedState.items) ? parsedState.items : parsedState.key ? [parsedState] : [];
    let itemKey = options.itemKey || parsedState.key;
    if (options.variantId) {
      const matches = items.filter(
        (item) =>
          Number(item.variant_id || item.id) === Number(options.variantId) &&
          !item.parent_relationship?.parent &&
          !item.parent_relationship?.parent_key
      );
      const exactMatch = matches.find((item) => item.key === itemKey);
      // Cart discounts can change a line key. Only use a unique variant match.
      itemKey = exactMatch?.key || (matches.length === 1 ? matches[0].key : undefined);
    } else if (!itemKey && !Object.hasOwn(parsedState, 'item_count') && items.length === 1) {
      itemKey = items[0].key;
    }
    this.cartItemKey = itemKey;

    const heading = options.message || this.defaultHeading;
    if (this.heading) this.heading.textContent = heading;
    if (heading) this.notification.setAttribute('aria-label', heading);
    const successIcon = this.querySelector('.cart-notification__success-icon');
    if (successIcon) {
      successIcon.hidden = options.success === false;
      successIcon.style.display = options.success === false ? 'none' : '';
    }

    // An omitted section must not leave a previous product or shipping claim visible.
    const productTarget = document.getElementById('cart-notification-product');
    if (productTarget) {
      productTarget.replaceChildren();
      const fallback = document.createElement('p');
      fallback.textContent = options.fallbackMessage || 'View your cart to confirm the updated items.';
      productTarget.appendChild(fallback);
    }
    document.getElementById('cart-notification-shipping')?.replaceChildren();
    const cartLink = document.getElementById('cart-notification-button');
    if (cartLink) cartLink.textContent = this.defaultCartLinkText;

    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      const sectionHTML = parsedState.sections?.[section.id];

      // Optional cart UI sections must never turn a successful add request
      // into a customer-facing cart error. Shopify can occasionally omit a
      // section response in preview/editor sessions or on a slow connection.
      if (!target || typeof sectionHTML !== 'string') return;
      if (section.id === 'cart-notification-product' && !this.cartItemKey) return;

      const innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
      if (innerHTML !== null) target.innerHTML = innerHTML;
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-notification-shipping',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return section ? section.innerHTML : null;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
